/**
 * The announcer (A-5, R26).
 *
 * Uses the browser's `SpeechSynthesis`, filtered through Web Audio for a radio
 * comms effect where the platform allows it, and **degrades silently to nothing
 * where it does not**. This is the one place in the game where output varies by
 * platform, and that is a committed decision rather than a deferred one: the
 * formant-synthesis alternative would double the work and hand the call to a
 * listening test no automated gate can perform.
 *
 * `SpeechSynthesis` cannot be routed through an `AudioContext` in any browser,
 * so the radio treatment is applied by voice selection and prosody rather than
 * by filtering. The band-passed click that accompanies each line is synthesized
 * and *does* go through the mixer, which is what actually sells "radio".
 */

import { Synth } from './synth.js'

export const VOX = {
  ConstructionComplete: 'Construction complete',
  UnitReady: 'Unit ready',
  InsufficientFunds: 'Insufficient funds',
  BaseUnderAttack: 'Our base is under attack',
  LowPower: 'Low power',
  UnitLost: 'Unit lost',
  StructureLost: 'Structure lost',
  SuperweaponReady: 'Superweapon ready',
  MissionAccomplished: 'Mission accomplished',
  MissionFailed: 'Mission failed',
} as const

export type VoxLine = (typeof VOX)[keyof typeof VOX]

export const ALL_VOX: VoxLine[] = Object.values(VOX)

/** Minimum seconds between two announcements, so alerts do not pile up. */
const MIN_GAP = 1.4
/** Seconds before the same line may repeat. */
const REPEAT_COOLDOWN = 8

export class Announcer {
  private available = false
  private voice: SpeechSynthesisVoice | null = null
  private lastSpokeAt = -Infinity
  private readonly lastLineAt = new Map<string, number>()
  /** Lines spoken, for the test — asserting silence is otherwise untestable. */
  readonly spoken: string[] = []

  constructor(
    private readonly synth: Synth,
    private readonly destination: AudioNode,
    private readonly now: () => number,
  ) {
    this.detect()
  }

  private detect(): void {
    try {
      this.available =
        typeof globalThis !== 'undefined' &&
        typeof (globalThis as { speechSynthesis?: unknown }).speechSynthesis !== 'undefined'
      if (!this.available) return
      const pick = () => {
        const voices = speechSynthesis.getVoices()
        // Prefer a clipped, low-warmth voice; fall back to whatever exists.
        this.voice =
          voices.find((v) => /en[-_]GB/i.test(v.lang) && /male|daniel|arthur/i.test(v.name)) ??
          voices.find((v) => /^en/i.test(v.lang)) ??
          voices[0] ??
          null
      }
      pick()
      speechSynthesis.addEventListener?.('voiceschanged', pick)
    } catch {
      // A platform without speech is a platform that plays in silence, not one
      // that throws on boot.
      this.available = false
    }
  }

  get isAvailable(): boolean {
    return this.available
  }

  /**
   * Speak a line. Rate-limited, de-duplicated, and safe to call from an event
   * handler that fires forty times a second.
   */
  say(line: VoxLine, priority = false): void {
    const t = this.now()
    if (!priority && t - this.lastSpokeAt < MIN_GAP) return
    if (t - (this.lastLineAt.get(line) ?? -Infinity) < REPEAT_COOLDOWN) return
    this.lastSpokeAt = t
    this.lastLineAt.set(line, t)
    this.spoken.push(line)

    // The radio click goes through the mixer whether or not speech works, so
    // the game still acknowledges the event on a silent platform.
    this.radioClick()

    if (!this.available) return
    try {
      const utterance = new SpeechSynthesisUtterance(line)
      if (this.voice) utterance.voice = this.voice
      utterance.rate = 1.08
      utterance.pitch = 0.85
      utterance.volume = 0.85
      speechSynthesis.speak(utterance)
    } catch {
      // Degrade to silence rather than to an exception.
      this.available = false
    }
  }

  /** A short band-passed burst, the way a squelch tail reads as a radio. */
  private radioClick(): void {
    const t = this.synth.ctx.currentTime
    this.synth.noiseBurst({
      peak: 0.1,
      env: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.03 },
      filter: 'bandpass',
      frequency: 1800,
      q: 3,
      start: t,
      destination: this.destination,
    })
  }

  cancel(): void {
    if (!this.available) return
    try {
      speechSynthesis.cancel()
    } catch {
      this.available = false
    }
  }
}
